import { graphql, type HeadProps, type PageProps } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, type FC } from "react";
import BackgroundTheme from "../../components/general/BackgroundTheme";
import GridContainer from "../../components/general/GridContainer";
import ModulesRenderer from "../../components/module-renderer";
import { Breadcrumbs } from "../../containers/breadcrumbs";
import { HeadMetaData } from "../../containers/headMetaData";
import PageContainerComponent from "../../containers/page";
import { SEO } from "../../containers/seo";
import { useAppState } from "../../context/app/context";
import type { CategoryPageProps } from "../../gatsby-utils/builders/pages/create-category-pages";
import { useStoryblokPreviewData } from "../../hooks/use-storyblok-preview";
import type { Mutable } from "../../type-definitions/utility-types";
import { generateCanonicalUrl } from "../../utils/canonicalUrl";
import { pushCustomGTMEvent } from "../../utils/gtm/gtm-handlers";
import {
	findTranslatedSlugNameFromLinkForSite,
	findTranslatedSlugPathFromLinkForSite,
} from "../../utils/storyblok/utils";

const CategoryPage: FC<
	PageProps<Queries.CategoryPageQuery, CategoryPageProps>
> = ({ data }) => {
	const { t } = useTranslation();
	const page = data.page;
	const pageContent = useStoryblokPreviewData({
		...page,
		_uid: page?._uid ?? "",
		component: page?.component ?? "",
	});

	const modules =
		pageContent?.modules && pageContent.modules.length > 0
			? pageContent.modules.map((module) => module as Mutable<typeof module>)
			: [];

	useEffect(() => {
		pushCustomGTMEvent({ type: "setPageType", data: { pageType: "category" } });
	}, []);

	if (!pageContent) return null;

	const currentPageLink = findTranslatedSlugPathFromLinkForSite(
		data.breadcrumbs,
	);
	const currentPageName = findTranslatedSlugNameFromLinkForSite(
		data.breadcrumbs,
	);

	const breadcrumbs = currentPageName
		? [
				{ link: "/", label: t("Home") },
				{
					link: currentPageLink,
					label: currentPageName,
				},
			]
		: [];

	const theme = pageContent.background_color ?? undefined;

	return (
		<PageContainerComponent>
			<BackgroundTheme themeColor={theme} />
			<Breadcrumbs breadcrumbs={breadcrumbs} />
			<GridContainer role="main">
				<ModulesRenderer modules={modules} />
			</GridContainer>
		</PageContainerComponent>
	);
};

export const query = graphql`
	query CategoryPage(
		$storyblokUuid: String
		$language: String!
		$storyblokLanguage: String!
	) {
		...Locales

		page: storyblokEntry(
			lang: { eq: $storyblokLanguage }
			field_component: { eq: "product_category" }
			uuid: { eq: $storyblokUuid }
		) {
			uuid
			_uid
			name
			background_color
			component: field_component
			...Modules
			commercetools_category {
				key
				name
				slug
			}
			...SeoMetaData
		}

		breadcrumbs: storyblokLink(uuid: { eq: $storyblokUuid }) {
			real_path
			name
			alternates {
				path
				name
				lang
			}
		}
	}
`;

export default CategoryPage;

export const Head: FC<HeadProps<Queries.CategoryPageQuery, PageProps>> = (
	props,
) => {
	const page: Mutable<typeof props.data.page> = props.data.page;
	const name = page?.commercetools_category?.name || page?.name;

	const { siteConfig } = useAppState();

	const cannonicalUrl = generateCanonicalUrl(
		siteConfig.name,
		props.location.pathname,
	);

	return (
		<>
			<HeadMetaData />
			<SEO
				localesData={props.data.locales}
				title={page?.meta_title || name || ""}
				description={page?.meta_description || ""}
				noIndex={Boolean(page?.no_index)}
				noFollow={Boolean(page?.no_follow)}
				canonicalUrl={cannonicalUrl}
			/>
		</>
	);
};
